// Ported 1:1 from ~/Desktop/rondo/src/components/ui/DeltaNumber.tsx.

import { TrendUp, TrendDown } from '@phosphor-icons/react'
import { AnimateNumber } from 'motion-plus/react'

export type DeltaFormat = 'percent' | 'usd'
export type DeltaSize = 'sm' | 'md' | 'lg'
export type DeltaTone = 'signed' | 'neutral'

interface DeltaNumberProps {
  value: number
  format: DeltaFormat
  compoundPercent?: number
  size?: DeltaSize
  tone?: DeltaTone
  showTrendIcon?: boolean
  className?: string
  animate?: boolean
  /**
   * Decimal places for the USD value. Defaults to 0 to match Rondo. Trading
   * Grounds passes 2 for stake/PnL displays per PLAN.md §4.2.
   */
  precision?: number
}

function formatUsdSigned(n: number, precision: number): string {
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  })
  if (n > 0) return `+$${abs}`
  if (n < 0) return `-$${abs}`
  return `$${abs}`
}

function formatPctSigned(n: number): string {
  if (n > 0) return `+${n.toFixed(1)}%`
  if (n < 0) return `${n.toFixed(1)}%`
  return '0.0%'
}

const SIZE_CLS: Record<DeltaSize, string> = {
  sm: 'text-label',
  md: 'text-label',
  lg: 'text-body',
}

const ICON_PX: Record<DeltaSize, number> = {
  sm: 12,
  md: 14,
  lg: 16,
}

export function DeltaNumber({
  value,
  format,
  compoundPercent,
  size = 'md',
  tone = 'signed',
  showTrendIcon = false,
  className,
  animate = false,
  precision = 0,
}: DeltaNumberProps) {
  const signedToneCls = value > 0 ? 'text-green' : value < 0 ? 'text-red' : 'text-grey'
  const toneCls = tone === 'neutral' ? 'text-black' : signedToneCls

  const iconPx = ICON_PX[size]
  const icon =
    showTrendIcon && value !== 0 ? (
      value > 0 ? (
        <TrendUp size={iconPx} weight="bold" className="text-green shrink-0" />
      ) : (
        <TrendDown size={iconPx} weight="bold" className="text-red shrink-0" />
      )
    ) : null

  const rootCls =
    (icon ? 'inline-flex items-center gap-1 ' : '') +
    'tabular-nums ' +
    toneCls +
    ' ' +
    SIZE_CLS[size] +
    (className ? ' ' + className : '')

  const signPrefix = value > 0 ? '+' : value < 0 ? '-' : ''
  const unitPrefix = format === 'usd' ? '$' : ''
  const unitSuffix = format === 'percent' ? '%' : ''

  const primaryNode = animate ? (
    <AnimateNumber
      prefix={`${signPrefix}${unitPrefix}`}
      suffix={unitSuffix}
      locales="en-US"
      format={
        format === 'usd'
          ? { minimumFractionDigits: precision, maximumFractionDigits: precision }
          : { minimumFractionDigits: 1, maximumFractionDigits: 1 }
      }
      transition={{ duration: 0.2, ease: 'easeOut' }}
    >
      {Math.abs(value)}
    </AnimateNumber>
  ) : format === 'usd' ? (
    formatUsdSigned(value, precision)
  ) : (
    formatPctSigned(value)
  )

  return (
    <span className={rootCls}>
      {icon}
      <span>
        {primaryNode}
        {compoundPercent != null && <> ({formatPctSigned(compoundPercent)})</>}
      </span>
    </span>
  )
}
